import {GiuXAccumuloRecord} from "@commons/mapping/GiuXAccumuloRecord";
import {TabellaAzienda} from "@commons/mapping/TabellaAzienda";
import {ValoreAccumuloParams} from "@commons/services/presenze_api/valoriAccumuli.api";
import {FPNetDate, ObjectFactory} from "@commons/types";
import {CausaliGiornata, PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {StatoWeekPLUSRecord} from "@commons/mapping/StatoWeekPLUSRecord";
import {DateTime} from "luxon";
import {merge} from "lodash";

export type TipoCheckPresenze = "NESSUNO_CHK" | "WARNING" | "ERROR" | "SOLO_CHK";
export type Regola =
    "UGUALE_A"
    | "MINORE"
    | "MINORE_UGUALE"
    | "MAGGIORE"
    | "MAGGIORE_UGUALE"
    | "MINORE_PREV"
    | "MAGGIORE_PREV"
    | "DIVERSO_DA"
    | "UGUALE_A_VOCE"
    | "DIVERSO_DA_VOCE"
    | "MINORE_VOCE"
    | "MINORE_UGUALE_VOCE"
    | "MAGGIORE_VOCE"
    | "MAGGIORE_UGUALE_VOCE"
    | "UGUALE_PREV"
    | "DIVERSO_PREV";
export type Periodo = "SETTIMANA" | "MESE" | "ANNO" | "VALIDITA" | "GIORNO";

export interface AccumuloRecord extends TabellaAzienda {
    idAziendaMaster: number;
    tipoCheckPresenze: TipoCheckPresenze;
    regola: Regola;
    valore: number;
    codVoce: number;
    periodo: Periodo;
    validitaInizio: FPNetDate;
    validitaFine: FPNetDate;
    listaGiu: GiuXAccumuloRecord[];
    listaStatus: string[];

    params: ValoreAccumuloParams;
    valoreVoce: number;
    saldoIniziale: number,
    saldoPeriodo: number,

}

export class AccumuloRecord {
    static factory: ObjectFactory<AccumuloRecord> = (json?: Partial<AccumuloRecord>) => {
        return merge<AccumuloRecord, Partial<AccumuloRecord> | undefined>({
            id: '',
            descrizione: '',
            idAzienda: 0,
            idTipo: 0,
            idAziendaMaster: 0,
            tipoCheckPresenze: 'NESSUNO_CHK',
            regola: 'UGUALE_A',
            valore: 0,
            codVoce: 0,
            periodo: 'GIORNO',
            validitaInizio: null,
            validitaFine: null,
            listaGiu: [],
            listaStatus: [],
            params: {} as ValoreAccumuloParams,
            valoreVoce: 0,
            saldoIniziale: 0,
            saldoPeriodo: 0,
            URI: null,
        }, json);
    }

    static getDescrizione = (that: AccumuloRecord) => that.descrizione;

    static applicaCriterio = (that: AccumuloRecord, saldo: number, dayTotali?: RiepilogoOrePlus) => {
        let result = false;
        const quantita = that.regola.indexOf('_VOCE') > 0 ? that.valoreVoce : that.valore;
        switch (that.regola) {
            case 'UGUALE_A':
            case 'UGUALE_A_VOCE':
                result = (saldo === quantita);
                break;
            case 'DIVERSO_DA':
            case 'DIVERSO_DA_VOCE':
                result = (saldo !== quantita);
                break;
            case 'MINORE':
            case 'MINORE_VOCE':
                result = (saldo < quantita);
                break;
            case 'MINORE_UGUALE':
            case 'MINORE_UGUALE_VOCE':
                result = (saldo <= quantita);
                break;
            case 'MAGGIORE':
            case 'MAGGIORE_VOCE':
                result = (saldo > quantita);
                break;
            case 'MAGGIORE_UGUALE':
            case 'MAGGIORE_UGUALE_VOCE':
                result = (saldo >= quantita);
                break;

            case 'MINORE_PREV':
                if (dayTotali) {
                    result = (saldo < RiepilogoOrePlus.getTotaleHPrev(dayTotali) / 60);
                }
                break;
            case 'MAGGIORE_PREV':
                if (dayTotali) {
                    result = (saldo > RiepilogoOrePlus.getTotaleHPrev(dayTotali) / 60);
                }
                break;
            case 'UGUALE_PREV':
                if (dayTotali) {
                    result = (saldo === RiepilogoOrePlus.getTotaleHPrev(dayTotali) / 60);
                }
                break;
            case 'DIVERSO_PREV':
                if (dayTotali) {
                    result = (saldo !== RiepilogoOrePlus.getTotaleHPrev(dayTotali) / 60);
                }
                break;
        }
        return result;
    };

    static hasGiuAccumulo = (that: AccumuloRecord, day: PresenzeGiornataPlusRecord) => {
        let applicaControllo = false;
        for (let indGiu = 0; indGiu < that.listaGiu.length; ++indGiu) {
            const giuAccumulo = that.listaGiu[indGiu];
            for (let indCausale = 0; indCausale < day.causaliHH.length; ++indCausale) {
                const causale = day.causaliHH[indCausale];
                if (causale && causale.codGiu === giuAccumulo.id) {
                    applicaControllo = true;
                    break;
                }
            }
        }
        return applicaControllo;
    };

    static _calcolaSaldo(accumulo: AccumuloRecord, _day: PresenzeGiornataPlusRecord, giornata: PresenzeGiornataPlusRecord, _indexGiornata: number, _weekRif: StatoWeekPLUSRecord | null, _soloMeseRif: boolean) {
        let saldoPeriodo = 0;
        const totalizza = (giuAccumulo: GiuXAccumuloRecord, causali: CausaliGiornata): void => {
            for (let indCausale = 0; indCausale < causali.length; ++indCausale) {
                const causale = causali[indCausale];
                if (causale && causale.codGiu === giuAccumulo.id) {
                    switch (giuAccumulo.operazione) {
                        case 1: // +
                            saldoPeriodo += causale.quantita;
                            break;
                        case 2: // -
                            saldoPeriodo -= causale.quantita;
                            break;
                        case 3: // =
                            break;
                        case 4: // conta
                            saldoPeriodo += 1;
                            break;
                        default:
                            break;
                    }
                }
            }
        };

        for (let indGiu = 0; indGiu < accumulo.listaGiu.length; ++indGiu) {
            const giuAccumulo: GiuXAccumuloRecord = accumulo.listaGiu[indGiu];

            totalizza(giuAccumulo, giornata.causaliHH);
            totalizza(giuAccumulo, giornata.causaliQI);
        }

        return saldoPeriodo;
    }

    static isValidInPeriodo = (that: AccumuloRecord, day: PresenzeGiornataPlusRecord): boolean => {
        const dtGiornata = DateTime.fromISO(day.dtGiornata!);
        if (that.validitaInizio === null && that.validitaFine === null) {
            return true;
        } else if (that.validitaInizio === null && that.validitaFine !== null) {
            return dtGiornata <= DateTime.fromISO(that.validitaFine);
        } else if (that.validitaInizio !== null && that.validitaFine === null) {
            return dtGiornata >= DateTime.fromISO(that.validitaInizio);
        } else {
            return dtGiornata >= DateTime.fromISO(that.validitaInizio!) && dtGiornata <= DateTime.fromISO(that.validitaFine!);
        }
    }

    static getCriterio = (that: AccumuloRecord, unicode: boolean = true) => {

        const quantita = that.regola.indexOf('_VOCE') > 0 ? that.valoreVoce : that.valore;
        let regola = '???';
        switch (that.regola) {
            case 'UGUALE_A':
            case 'UGUALE_A_VOCE':
                regola = '=';
                break;
            case 'DIVERSO_DA':
            case 'DIVERSO_DA_VOCE':
                regola = unicode ? '&#8800;' : '≠';
                break;
            case 'MINORE':
            case 'MINORE_VOCE':
                regola = unicode ? '&#60;' : '<';
                break;
            case 'MINORE_UGUALE':
            case 'MINORE_UGUALE_VOCE':
                regola = unicode ? '&#8804;' : '<=';
                break;
            case 'MAGGIORE':
            case 'MAGGIORE_VOCE':
                regola = unicode ? '&#62;' : '>';
                break;
            case 'MAGGIORE_UGUALE':
            case 'MAGGIORE_UGUALE_VOCE':
                regola = unicode ? '&#8805;' : '>=';
                break;
            case 'MINORE_PREV':
                break;
            case 'MAGGIORE_PREV':
                break;

        }
        if (that.tipoCheckPresenze === 'WARNING') {
            return 'Warning se ' + regola + ' ' + quantita;
        } else if (that.tipoCheckPresenze === 'ERROR') {
            return 'Anomalia se ' + regola + ' ' + quantita;
        } else {
            return 'Vero se ' + regola + ' ' + quantita;
        }
    };
}
